import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  BarChart3,
  Users,
  Banknote,
  UserCircle,
  ShieldCheck,
  Building2,
  LogOut,
  Menu,
  X,
  TicketPlus,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { SetupCompleteChip } from "./OnboardingChecklist";
import { eventsApi } from "../lib/events";
import ManualIssueDialog from "./ManualIssueDialog";

const NAV = [
  { to: "/dashboard/events", label: "My Events", icon: CalendarDays, testid: "nav-my-events" },
  { to: "/dashboard/venues", label: "Venues", icon: Building2, testid: "nav-venues" },
  { to: "/dashboard/sales", label: "Sales", icon: BarChart3, testid: "nav-sales" },
  { to: "/dashboard/attendees", label: "Attendees", icon: Users, testid: "nav-attendees" },
  { to: "/dashboard/payouts", label: "Payouts", icon: Banknote, testid: "nav-payouts" },
  { to: "/dashboard/profile", label: "Profile", icon: UserCircle, testid: "nav-profile" },
];

const ADMIN_NAV = {
  to: "/dashboard/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin",
};

const PAGE_TITLES = {
  "/dashboard/events": "My Events",
  "/dashboard/venues": "Venues",
  "/dashboard/sales": "Sales",
  "/dashboard/attendees": "Attendees",
  "/dashboard/payouts": "Payouts",
  "/dashboard/profile": "Profile",
  "/dashboard/admin": "Admin",
};

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chipEvents, setChipEvents] = useState([]);
  const [showIssueTickets, setShowIssueTickets] = useState(false);

  // Sprint AJ — lightweight fetch so the sidebar chip can decide whether
  // to render. Reuses the same /events/mine endpoint; failure is silent.
  useEffect(() => {
    let alive = true;
    if (!user?.id) return () => { alive = false; };
    eventsApi
      .listCached()
      .then((data) => alive && setChipEvents(Array.isArray(data) ? data : []))
      .catch(() => { /* chip is optional; ignore */ });
    return () => { alive = false; };
  }, [user?.id]);

  const pageTitle = PAGE_TITLES[location.pathname] || "Dashboard";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const displayName = user?.name || "Organiser";
  const initials = displayName
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-white text-[var(--gt-ink)]" data-testid="dashboard-root">
      <div className="lg:grid lg:grid-cols-[280px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside
          className={`${
            mobileOpen ? "block" : "hidden"
          } lg:block bg-[var(--gt-bg-marketing)] border-r border-[var(--gt-line)] lg:h-screen lg:sticky lg:top-0 flex flex-col p-6`}
          data-testid="dashboard-sidebar"
        >
          <div className="flex items-center justify-between mb-10">
            <Logo size="md" to="/dashboard/events" />
            <button
              className="lg:hidden gt-btn-ghost p-2"
              onClick={() => setMobileOpen(false)}
              data-testid="sidebar-close-btn"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          <div className="gt-label mb-4">Workspace</div>
          <nav className="flex flex-col gap-1 mb-auto" data-testid="sidebar-nav">
            {NAV.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm border transition-all duration-150 ${
                    isActive
                      ? "bg-white border-[var(--gt-line)] shadow-sm font-bold text-[var(--gt-ink)]"
                      : "border-transparent text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] hover:bg-[var(--gt-line)]/40"
                  }`
                }
              >
                <Icon size={18} strokeWidth={2} />
                <span className="text-sm">{label}</span>
              </NavLink>
            ))}

            {user?.is_admin && (
              <NavLink
                to={ADMIN_NAV.to}
                data-testid={ADMIN_NAV.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `mt-3 flex items-center gap-3 px-3 py-2.5 rounded-sm border transition-all duration-150 ${
                    isActive
                      ? "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)] font-bold"
                      : "border-[var(--gt-line)] text-[var(--gt-ink)] hover:bg-[var(--gt-line)]/40"
                  }`
                }
              >
                <ADMIN_NAV.icon size={18} strokeWidth={2} />
                <span className="text-sm">{ADMIN_NAV.label}</span>
              </NavLink>
            )}
            {user?.is_admin && (
              <button
                type="button"
                onClick={() => { setShowIssueTickets(true); setMobileOpen(false); }}
                data-testid="nav-issue-tickets"
                className="flex items-center gap-3 px-3 py-2.5 rounded-sm border border-[var(--gt-line)] text-[var(--gt-ink)] hover:bg-[var(--gt-line)]/40 transition-all duration-150 text-left"
              >
                <TicketPlus size={18} strokeWidth={2} />
                <span className="text-sm">Issue Tickets</span>
              </button>
            )}
          </nav>

          <div className="mt-8 pt-6 border-t border-[var(--gt-line)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[var(--gt-ink)] text-white flex items-center justify-center font-heading font-bold text-sm">
                {initials || "GT"}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--gt-ink)] truncate" data-testid="sidebar-user-name">
                  {displayName}
                </div>
                <div className="text-[11px] tracking-[0.15em] uppercase text-[var(--gt-ink-3)]">
                  Australian Account
                </div>
              </div>
            </div>
            <SetupCompleteChip user={user} events={chipEvents} />
          </div>
        </aside>

        {/* Main column */}
        <div className="flex flex-col min-h-screen">
          {/* Topbar */}
          <header
            className="h-16 border-b border-[var(--gt-line)] bg-white flex items-center justify-between px-6 sm:px-8 sticky top-0 z-30"
            data-testid="dashboard-topbar"
          >
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden gt-btn-ghost p-2"
                onClick={() => setMobileOpen(true)}
                data-testid="sidebar-open-btn"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
              <h1
                className="font-heading text-xl sm:text-2xl font-bold tracking-tight"
                data-testid="page-title"
              >
                {pageTitle}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-sm text-[var(--gt-ink-2)]" data-testid="topbar-user-email">
                {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="gt-btn-ghost"
                data-testid="logout-btn"
              >
                <LogOut size={16} />
                <span className="text-sm">Log out</span>
              </button>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 bg-white" data-testid="dashboard-content">
            <div className="gt-fade-in">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      {showIssueTickets && (
        <ManualIssueDialog
          open
          onClose={() => setShowIssueTickets(false)}
        />
      )}
    </div>
  );
}
